/**
 * Allowances & SAP codes — the two employee fields Finance owns.
 *
 * Deliberately a Finance screen rather than part of the Organization Chart: a
 * directory administrator maintains who reports to whom, and Finance decides
 * what someone may claim and which vendor account pays them. The server
 * enforces that split; this page is where the people who hold it work.
 *
 * Both figures are advisory. Nothing here blocks a claim — the allowance is
 * shown to the reviewer at Finance Review, and the vendor code rides onto the
 * export.
 */
import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import { money } from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { ActionDialog, ErrorNote, QueuePanel, useAction } from '@/components/QueueKit';
import { LoadingBlock, Spinner } from '@/components/Spinner';
import { useToast } from '@/components/Toast';
import { downloadWorkbook, readFirstSheet, todayStamp, type CellValue } from '@/components/admin/sheets';
import '@/styles/pages.css';

interface EmployeeRow {
  id: string;
  empId: string;
  name: string;
  email: string;
  department: string;
  position: string;
  monthlyAllowance: number | null;
  sapVendorCode: string | null;
}

/** One upload carries both fields, which is what Finance asked for. */
const TEMPLATE_HEADERS = ['Employee ID', 'Company Email', 'Monthly Allowance', 'SAP Vendor Code'] as const;

interface UploadRow {
  empId: string;
  email: string;
  monthlyAllowance?: number | null;
  sapVendorCode?: string | null;
}

interface BulkResult {
  updated: number;
  validCount: number;
  errors: Array<{ row: number; who: string; errors: string[] }>;
}

function cell(row: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
}

/**
 * An empty cell means "leave this alone", not "clear it".
 *
 * The distinction matters more than it looks: a file listing only the vendor
 * codes would otherwise wipe every allowance that was set one at a time. To
 * clear a value deliberately, the cell says `-`.
 */
function optionalField(raw: string): string | null | undefined {
  if (raw === '') return undefined;
  if (raw === '-') return null;
  return raw;
}

function toUploadRows(raw: Array<Record<string, unknown>>): UploadRow[] {
  return raw.map((r) => {
    const allowanceText = optionalField(cell(r, 'Monthly Allowance', 'monthlyAllowance'));
    const code = optionalField(cell(r, 'SAP Vendor Code', 'sapVendorCode'));
    return {
      empId: cell(r, 'Employee ID', 'empId'),
      email: cell(r, 'Company Email', 'email').toLowerCase(),
      ...(allowanceText === undefined
        ? {}
        : { monthlyAllowance: allowanceText === null ? null : Number(allowanceText) }),
      ...(code === undefined ? {} : { sapVendorCode: code }),
    };
  });
}

export function EmployeeFinancePage(): ReactNode {
  const { toast } = useToast();
  const [rows, setRows] = useState<EmployeeRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [editing, setEditing] = useState<EmployeeRow | null>(null);
  const [allowance, setAllowance] = useState('');
  const [vendorCode, setVendorCode] = useState('');
  const action = useAction();

  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      setRows(await api.employeeFinance.list<EmployeeRow[]>());
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows ?? [];
    return (rows ?? []).filter((r) =>
      [r.name, r.empId, r.email, r.department, r.sapVendorCode ?? ''].some((v) =>
        v.toLowerCase().includes(q),
      ),
    );
  }, [rows, search]);

  const openEdit = useCallback(
    (row: EmployeeRow): void => {
      setEditing(row);
      setAllowance(row.monthlyAllowance == null ? '' : String(row.monthlyAllowance));
      setVendorCode(row.sapVendorCode ?? '');
      action.setError(null);
    },
    [action],
  );

  const save = useCallback(async (): Promise<void> => {
    if (!editing) return;
    const trimmed = allowance.trim();
    const ok = await action.run(async () => {
      await api.employeeFinance.set<unknown>({
        userId: editing.id,
        // Blank clears the field; the server reads null as "no allowance set",
        // which is not the same as zero.
        monthlyAllowance: trimmed === '' ? null : Number(trimmed),
        sapVendorCode: vendorCode.trim() === '' ? null : vendorCode.trim(),
      });
    });
    if (!ok) return;
    toast(`Updated ${editing.name}.`, 'ok');
    setEditing(null);
    await load();
  }, [editing, allowance, vendorCode, action, toast, load]);

  const exportCurrent = useCallback(async (): Promise<void> => {
    const data: CellValue[][] = [
      [...TEMPLATE_HEADERS],
      ...(rows ?? []).map((r) => [
        r.empId,
        r.email,
        r.monthlyAllowance == null ? '' : r.monthlyAllowance,
        r.sapVendorCode ?? '',
      ]),
    ];
    await downloadWorkbook(`employee-allowances-${todayStamp()}.xlsx`, [
      { name: 'Allowances', rows: data },
    ]);
  }, [rows]);

  const columns: ReadonlyArray<Column<EmployeeRow>> = useMemo(
    () => [
      { key: 'empId', header: 'Employee ID', cell: (r) => r.empId },
      { key: 'name', header: 'Name', cell: (r) => <b>{r.name}</b> },
      { key: 'department', header: 'Department', cell: (r) => r.department },
      {
        key: 'allowance',
        header: 'Monthly allowance',
        align: 'right',
        cell: (r) =>
          r.monthlyAllowance == null ? <span className="muted">Not set</span> : money(r.monthlyAllowance),
      },
      {
        key: 'vendor',
        header: 'SAP vendor code',
        cell: (r) => r.sapVendorCode ?? <span className="muted">Not set</span>,
      },
      {
        key: 'actions',
        header: '',
        cell: (r) => (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(r)}>
            Edit
          </button>
        ),
      },
    ],
    [openEdit],
  );

  if (rows === null && error === null) return <LoadingBlock label="Loading employees…" />;

  const withAllowance = (rows ?? []).filter((r) => r.monthlyAllowance != null).length;
  const withCode = (rows ?? []).filter((r) => r.sapVendorCode).length;

  return (
    <>
      <ErrorNote message={error} />

      <QueuePanel
        title="Allowances & SAP codes"
        count={rows?.length ?? 0}
        actions={
          <>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => void exportCurrent()}>
              ⬇ Export
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setUploadOpen(true)}>
              Bulk upload
            </button>
          </>
        }
      >
        <p className="hint">
          {withAllowance} of {rows?.length ?? 0} have an allowance, {withCode} have a vendor code.
          Both are advisory: an allowance is shown to the reviewer and never blocks a claim, and a
          claim can be released without a vendor code.
        </p>

        <div className="field" style={{ maxWidth: 320 }}>
          <label htmlFor="ef-search">Search</label>
          <input
            id="ef-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, employee ID, email or code"
          />
        </div>

        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(r) => r.id}
          empty={<EmptyState icon="💳" title="No employee matches that search." />}
        />
      </QueuePanel>

      {editing ? (
        <ActionDialog
          title={`${editing.name} — ${editing.empId}`}
          confirmLabel="Save"
          onConfirm={() => void save()}
          onCancel={() => setEditing(null)}
          busy={action.busy}
          error={action.error}
        >
          <div className="field">
            <label htmlFor="ef-allowance">Monthly allowance</label>
            <input
              id="ef-allowance"
              type="number"
              step="0.01"
              min="0"
              value={allowance}
              onChange={(e) => setAllowance(e.target.value)}
              placeholder="Leave blank for no allowance"
            />
            <p className="hint">
              What this employee may claim in a calendar month. Shown at Finance Review beside the
              claim; it does not block a submission.
            </p>
          </div>

          <div className="field">
            <label htmlFor="ef-vendor">SAP vendor code</label>
            <input
              id="ef-vendor"
              value={vendorCode}
              maxLength={40}
              onChange={(e) => setVendorCode(e.target.value)}
              placeholder="Leave blank if none"
            />
            <p className="hint">
              Carried onto the Finance export. Each code identifies one payee, so no two employees
              may share one.
            </p>
          </div>
        </ActionDialog>
      ) : null}

      {uploadOpen ? (
        <BulkFinanceUpload
          onClose={() => setUploadOpen(false)}
          onDone={async () => {
            setUploadOpen(false);
            await load();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The bulk path, dry-run first.
 *
 * The server writes nothing unless every row is valid, so offering "Apply"
 * while errors remain would promise something it will not do.
 */
function BulkFinanceUpload({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode {
  const { toast } = useToast();
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<UploadRow[] | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onFile = useCallback(async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const parsed = toUploadRows(await readFirstSheet(file));
      setRows(parsed);
      const preview = await api.employeeFinance.bulkSet<BulkResult>({ rows: parsed, dryRun: true });
      setResult(preview);
    } catch (err) {
      setError(errorMessage(err));
      setRows(null);
    } finally {
      setBusy(false);
    }
  }, []);

  const apply = useCallback(async (): Promise<void> => {
    if (!rows) return;
    setBusy(true);
    try {
      const out = await api.employeeFinance.bulkSet<BulkResult>({ rows });
      toast(`Updated ${out.updated} employee record(s).`, 'ok');
      await onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [rows, toast, onDone]);

  const template = useCallback(async (): Promise<void> => {
    await downloadWorkbook('employee-allowance-template.xlsx', [
      {
        name: 'Allowances',
        rows: [[...TEMPLATE_HEADERS], ['GDEC-0099', 'juan.delacruz@greatdealscorp.com', 5000, 'V-100234']],
      },
    ]);
  }, []);

  const clean = result != null && result.errors.length === 0 && result.validCount > 0;

  return (
    <Modal title="Bulk upload — allowances and vendor codes" onClose={onClose} wide>
      {error ? <div className="form-error" role="alert">{error}</div> : null}

      <p className="hint">
        One file carries both fields. Match on Employee ID or company email — the upload only ever
        updates people who already exist, it never creates anyone. An empty cell leaves that value
        as it is; a single <b>-</b> clears it.
      </p>

      <div className="row-actions" style={{ marginBottom: 12 }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => void template()}>
          ⬇ Download template
        </button>
        <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer' }}>
          Choose file
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            hidden
            onChange={(e) => void onFile(e)}
          />
        </label>
        {fileName ? <span className="hint">{fileName}</span> : null}
        {busy ? <Spinner /> : null}
      </div>

      {result ? (
        <>
          <p className="hint">
            {result.validCount} row(s) ready, {result.errors.length} with a problem.
          </p>
          {result.errors.length > 0 ? (
            <ul className="upload-errors">
              {result.errors.map((e) => (
                <li key={`${e.row}-${e.who}`}>
                  <b>Row {e.row}</b> — {e.who}: {e.errors.join('; ')}
                </li>
              ))}
            </ul>
          ) : null}
          {clean ? (
            <button
              type="button"
              className="btn btn-good"
              onClick={() => void apply()}
              disabled={busy}
            >
              {busy ? <Spinner /> : null} Apply to {result.validCount} employee(s)
            </button>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}
